import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, ImagePlus, Loader2, PackageCheck, Upload, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { diamondsService, ItemCategoria } from '@/services/diamonds.service';

const IMAGE_BASE_URL = 'https://xdoinfinitto.com.br:3000';

interface ItemImage {
  fileUploaded: string | null;
  base64: string | null;
  fileType: string | null;
}

interface ItemDetail {
  id: number;
  nome: string;
  descricao: string;
  categoria_id: number | null;
  valor: number;
  custo: number;
  qntd: number;
  qntd_disponivel: number;
  qntd_resgatada: number;
  images: { fileUploaded: string }[];
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const itemSchema = z.object({
  nome: z.string().min(1, 'Nome obrigatório'),
  descricao: z.string().min(1, 'Descrição obrigatória'),
  categoria_id: z.coerce.number({ invalid_type_error: 'Categoria obrigatória' })
    .int().positive('Categoria obrigatória'),
  valor: z.coerce.number({ invalid_type_error: 'Valor obrigatório' })
    .int('Use número inteiro').min(1, 'Valor mínimo é 1'),
  qntd_disponivel: z.coerce.number({ invalid_type_error: 'Quantidade disponível obrigatória' })
    .int('Use número inteiro')
    .min(0, 'Quantidade deve ser >= 0'),
  custo: z.coerce.number({ invalid_type_error: 'Custo obrigatório' })
    .min(0, 'Custo deve ser >= 0'),
});

type EditarItemForm = z.infer<typeof itemSchema>;

export default function GerenciamentoItemEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categorias, setCategorias] = useState<ItemCategoria[]>([]);
  const [qntdResgatada, setQntdResgatada] = useState(0);
  const [imagesItem, setImagesItem] = useState<ItemImage[]>([
    { fileUploaded: null, base64: null, fileType: null },
    { fileUploaded: null, base64: null, fileType: null },
    { fileUploaded: null, base64: null, fileType: null },
  ]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<EditarItemForm>({
    resolver: zodResolver(itemSchema),
    mode: 'onChange',
    defaultValues: {
      nome: '',
      descricao: '',
      valor: 0,
      qntd_disponivel: 0,
      custo: 0,
    },
  });

  const loadItem = async (itemId: number) => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const response: any = await diamondsService.getDetailItem(itemId);
      const data: ItemDetail | undefined = response?.data;
      if (!data) {
        setLoadError(true);
        toast.error('Item não encontrado');
        return;
      }
      setQntdResgatada(data.qntd_resgatada ?? 0);
      reset({
        nome: data.nome ?? '',
        descricao: data.descricao ?? '',
        categoria_id: data.categoria_id ?? undefined,
        valor: data.valor ?? 0,
        qntd_disponivel: data.qntd_disponivel ?? Math.max(0, (data.qntd ?? 0) - (data.qntd_resgatada ?? 0)),
        custo: data.custo ?? 0,
      });
      const slots: ItemImage[] = [
        { fileUploaded: null, base64: null, fileType: null },
        { fileUploaded: null, base64: null, fileType: null },
        { fileUploaded: null, base64: null, fileType: null },
      ];
      (data.images ?? []).slice(0, 3).forEach((img, i) => {
        slots[i] = { fileUploaded: img.fileUploaded, base64: null, fileType: null };
      });
      setImagesItem(slots);
    } catch (error) {
      console.error('Erro ao buscar detalhes do item:', error);
      setLoadError(true);
      toast.error('Erro ao buscar detalhes do item');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!id || id === 'null' || id === 'undefined') {
      setLoadError(true);
      setIsLoading(false);
      return;
    }
    loadItem(Number(id));
    diamondsService.getItemsCategories()
      .then((response: any) => setCategorias(response?.data ?? []))
      .catch((err) => {
        console.error('Erro ao buscar categorias:', err);
        toast.error('Erro ao carregar categorias');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      setImagesItem((prev) => {
        const next = [...prev];
        next[index] = {
          fileUploaded: null,
          base64,
          fileType: file.type,
        };
        return next;
      });
    } catch {
      toast.error('Erro ao processar imagem');
    }
  };

  const removeImage = (index: number) => {
    setImagesItem((prev) => {
      const next = [...prev];
      next[index] = { fileUploaded: null, base64: null, fileType: null };
      return next;
    });
  };

  const onSubmit = async (values: EditarItemForm) => {
    if (!id) return;
    if (!imagesItem[0]?.fileUploaded && !imagesItem[0]?.base64) {
      toast.error('Imagem principal é obrigatória');
      return;
    }
    setIsSubmitting(true);
    try {
      const { qntd_disponivel, ...rest } = values;
      await diamondsService.updateItem(Number(id), { ...rest, qntd: qntd_disponivel + qntdResgatada }, imagesItem);
      toast.success('Item atualizado com sucesso!');
      await loadItem(Number(id));
    } catch (error) {
      console.error('Erro ao atualizar item:', error);
      toast.error('Erro ao atualizar item');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 p-6 text-center">
        <PackageCheck className="h-12 w-12 text-muted-foreground/40" />
        <p className="text-muted-foreground">Não foi possível carregar este item.</p>
        <Button variant="outline" onClick={() => navigate('/gerenciamento-itens')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar à lista
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-3xl mx-auto">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex justify-between items-start gap-3">
            <div>
              <CardTitle className="text-xl">Editar Item</CardTitle>
              <CardDescription className="mt-1">Atualize as informações, estoque e imagens do item.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/gerenciamento-itens')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
            </Button>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Informações básicas */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Informações Básicas</h3>
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input id="nome" className="mt-1" {...register('nome')} />
                {errors.nome && <p className="text-sm text-destructive mt-1">{errors.nome.message}</p>}
              </div>

              <div>
                <Label htmlFor="descricao">Descrição *</Label>
                <Textarea id="descricao" rows={3} className="mt-1 resize-none" {...register('descricao')} />
                {errors.descricao && <p className="text-sm text-destructive mt-1">{errors.descricao.message}</p>}
              </div>

              <div>
                <Label htmlFor="categoria_id">Categoria *</Label>
                <Controller
                  name="categoria_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={(v) => field.onChange(Number(v))}
                      value={field.value ? String(field.value) : ''}
                    >
                      <SelectTrigger id="categoria_id" className="mt-1">
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categorias.map((cat) => (
                          <SelectItem key={cat.id} value={String(cat.id)}>
                            {cat.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.categoria_id && <p className="text-sm text-destructive mt-1">{errors.categoria_id.message}</p>}
              </div>
            </div>

            <Separator />

            {/* Precificação e estoque */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Precificação e Estoque</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="custo">Custo (R$) *</Label>
                  <Input id="custo" type="number" min={0} step={0.01} className="mt-1" {...register('custo')} />
                  {errors.custo && <p className="text-sm text-destructive mt-1">{errors.custo.message}</p>}
                </div>
                <div>
                  <Label htmlFor="valor">Valor (Diamantes) *</Label>
                  <Input id="valor" type="number" min={1} step={1} className="mt-1" {...register('valor')} />
                  {errors.valor && <p className="text-sm text-destructive mt-1">{errors.valor.message}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="qntd_disponivel">Quantidade Disponível *</Label>
                <Input id="qntd_disponivel" type="number" min={0} step={1} className="mt-1" {...register('qntd_disponivel')} />
                {errors.qntd_disponivel && <p className="text-sm text-destructive mt-1">{errors.qntd_disponivel.message}</p>}
              </div>

              {/* Resumo de estoque */}
              <div className="grid grid-cols-3 gap-3 rounded-lg border bg-muted/40 p-4">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Resgatadas</p>
                  <p className="text-lg font-semibold">{qntdResgatada}</p>
                </div>
                <div className="text-center border-x">
                  <p className="text-xs text-muted-foreground mb-1">Disponíveis</p>
                  <p className="text-lg font-semibold text-primary">{Number(watch('qntd_disponivel')) || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Total</p>
                  <p className="text-lg font-semibold">{(Number(watch('qntd_disponivel')) || 0) + qntdResgatada}</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Imagens */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Imagens</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {imagesItem.map((img, i) => {
                  const previewSrc = img.base64
                    ? img.base64
                    : img.fileUploaded
                      ? `${IMAGE_BASE_URL}/diamonds/item-image/${img.fileUploaded}`
                      : null;
                  const isPrincipal = i === 0;
                  return (
                    <div
                      key={i}
                      className={`relative border rounded-xl p-3 flex flex-col items-center gap-3 transition-colors ${
                        isPrincipal ? 'border-primary/40 bg-primary/5' : 'bg-muted/20'
                      }`}
                    >
                      <div className="w-full flex items-center justify-between">
                        <span className="text-xs font-medium">
                          {isPrincipal ? 'Principal' : `Adicional ${i}`}
                        </span>
                        {isPrincipal && (
                          <Badge variant="secondary" className="text-[10px] h-5">Obrigatória</Badge>
                        )}
                      </div>
                      <div className="w-full aspect-square bg-muted rounded-lg flex items-center justify-center overflow-hidden border">
                        {previewSrc ? (
                          <img
                            src={previewSrc}
                            alt={`Imagem ${i + 1}`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const el = e.target as HTMLImageElement;
                              if (!el.src.endsWith('/placeholder.svg')) {
                                el.src = '/placeholder.svg';
                              }
                            }}
                          />
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <ImagePlus className="h-8 w-8" />
                            <span className="text-xs">Sem imagem</span>
                          </div>
                        )}
                      </div>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, i)}
                        className="text-xs cursor-pointer"
                      />
                      {!isPrincipal && previewSrc && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeImage(i)}
                          className="w-full text-destructive border-destructive/30 hover:bg-destructive/10 h-7"
                        >
                          <X className="h-3 w-3 mr-1" /> Remover
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/gerenciamento-itens')}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
